import { EditNote } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogProps, Typography } from '@mui/material';
import { SupportedLanguageCode } from '@procureai/schemas';
import { WidgetHeader, WidgetIcon } from '@procureai/ui-mui';
import { PropsWithChildren, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { languages } from '~/common/constants';
import { LanguageSelect } from '~/components/LanguageSelect/LanguageSelect';
import { MarkdownEditor } from '~/components/MarkdownEditor/MarkdownEditor';

type I18nEditDialogValue = Union.Nullable<Record<string, Union.Nullable<string>>>;

export interface I18nEditDialogProps {
  value?: I18nEditDialogValue;
  languageCode: SupportedLanguageCode;
  open: boolean;
  onClose: () => void;
  onChange?: (val: I18nEditDialogValue) => void;
}

export const I18nEditDialog = ({
  open,
  onClose,
  value: valueProp,
  languageCode,
  onChange,
}: PropsWithChildren<I18nEditDialogProps>) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'I18nEditDialog' });

  const [from, setFrom] = useState(languageCode);
  const [to, setTo] = useState(languageCode);

  const fromUpperCase = from.toUpperCase();
  const toUpperCase = to.toUpperCase();

  const [value, setValue] = useState<I18nEditDialogValue>(valueProp || {});

  // stop propagation of keydown events to prevent data-grit shortcuts from triggering
  const onKeyDown = useCallback<NonNullable<DialogProps['onKeyDown']>>((e) => {
    e.stopPropagation();
  }, []);

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose} onKeyDown={onKeyDown}>
      <WidgetHeader noDivider dense title={t('title')} icon={<WidgetIcon icon={<EditNote />} />} />
      <DialogContent dividers>
        <Box
          sx={{
            flexDirection: 'column',
            display: 'flex',
          }}
        >
          <Box
            sx={{
              flexDirection: 'row',
              display: 'flex',
              mb: 2,
            }}
          >
            <Box
              sx={{
                flex: 1,
                mr: 1,
              }}
            >
              <>
                <Typography variant="body2" color="textSecondary" align="center" gutterBottom>
                  {t('from.label')}
                </Typography>
                <LanguageSelect
                  value={from}
                  label={t('from.placeholder')}
                  items={languages}
                  onChange={(event) => {
                    setFrom(event.target.value as SupportedLanguageCode);
                  }}
                />
              </>
            </Box>
            <Box
              sx={{
                flex: 1,
                ml: 1,
              }}
            >
              <>
                <Typography variant="body2" color="textSecondary" align="center" gutterBottom>
                  {t('to.label')}
                </Typography>
                <LanguageSelect
                  value={to}
                  label={t('to.placeholder')}
                  items={languages}
                  onChange={(event) => {
                    setTo(event.target.value as SupportedLanguageCode);
                  }}
                />
              </>
            </Box>
          </Box>
          <Box
            sx={{
              flexDirection: 'row',
              display: 'flex',
              mb: 2,
            }}
          >
            <Box
              sx={{
                flex: 1,
                mr: 1,
              }}
            >
              <MarkdownEditor disableEdit value={value?.[fromUpperCase] || ''} />
            </Box>
            <Box
              sx={{
                flex: 1,
                ml: 1,
              }}
            >
              <MarkdownEditor
                value={value?.[toUpperCase] || ''}
                onChange={(value) => {
                  setValue((prev) => ({ ...prev, [toUpperCase]: value }));
                }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('cancelLabel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (onChange) {
              onChange(value);
            }
            onClose();
          }}
        >
          {t('confirmLabel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
